import { motion } from "framer-motion";
import { BatteryMedium, ShieldCheck, ShieldPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { PhotoGallery } from "./PhotoGallery";
import { categoryOf, warrantyOf, ACC_TYPES, accTypeOf, usesModels, conditionLabel, isNewCond, appleUntil } from "@/lib/store";
import { PriceBlock } from "./PriceBlock";
import { InterestAction } from "./InterestAction";
import { EASE } from "./Reveal";
import { FavoriteButton } from "./FavoriteButton";
import { HotBadge } from "./HotBadge";
import { AppleMark, isAllOriginal } from "./PartsInfo";

const Spec = ({ children }) => (
  <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-300">{children}</span>
);

export const ProductCard = ({ p, index, preferredModel }) => {
  const isNew = isNewCond(p?.condition);
  return (
    <motion.article
      data-testid={`product-card-${p.id}`}
      layout
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.9, delay: (index % 3) * 0.08, ease: EASE }}
      className="group relative flex flex-col rounded-[28px] border border-white/10 bg-[#111113] p-3 transition-colors duration-500 hover:border-white/25"
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-[20px] bg-zinc-950">
        <div className="absolute inset-0 spotlight opacity-80" aria-hidden="true" />
        <div className="relative h-full">
          <PhotoGallery images={p?.images} alt={p.model} id={p.id} to={`/produto/${p.id}`}
            dotsClass={p?.battery != null || isAllOriginal(p) ? "bottom-14 left-1/2 -translate-x-1/2" : "bottom-4 right-4"} />
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-4">
          <div className="flex flex-col items-start gap-1.5">
            <span data-testid={`product-condition-${p.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] ${isNew ? "bg-white text-black" : "bg-black/70 text-white border border-white/20 backdrop-blur"}`}>
              {conditionLabel(p)}
            </span>
            <HotBadge id={p.id} />
            {!isNew && categoryOf(p) !== "acessorios" && appleUntil(p) && (
              <span data-testid={`product-apple-warranty-badge-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/90 px-3 py-1 text-[11px] font-bold text-black backdrop-blur">
                <AppleMark className="h-3 w-3" /> Garantia Apple
                <span className="font-medium text-zinc-600">até {appleUntil(p).slice(3)}</span>
              </span>
            )}
          </div>
          <FavoriteButton p={p} className="pointer-events-auto -mr-1 -mt-1" />
        </div>
        {p?.battery != null && (
          <span className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-xs text-emerald-300 backdrop-blur">
            <BatteryMedium className="h-3.5 w-3.5" /> Bateria {p.battery}%
          </span>
        )}
        {isAllOriginal(p) && (
          <span data-testid={`product-original-seal-${p.id}`} title="Todas as peças originais de fábrica" className="pointer-events-none absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full border border-white/20 bg-black/70 px-3 py-1 text-xs font-medium text-white backdrop-blur">
            <AppleMark className="h-3 w-3" /> Original Apple
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col px-2 pt-4 pb-1">
        <Link to={`/produto/${p.id}`} data-testid={`product-link-${p.id}`} className="group/title w-fit">
          <h3 data-testid={`product-model-${p.id}`} className="font-display text-2xl font-bold tracking-tight text-white decoration-1 underline-offset-4 group-hover/title:underline">{p.model}</h3>
        </Link>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {p?.storage && <Spec>{p.storage}</Spec>}
          {p?.color && <Spec>{p.color}</Spec>}
          {categoryOf(p) === "acessorios" && <Spec>HPrime</Spec>}
          {categoryOf(p) === "acessorios" && ACC_TYPES.find((t) => t.id === accTypeOf(p)) && <Spec>{ACC_TYPES.find((t) => t.id === accTypeOf(p)).single}</Spec>}
          {accTypeOf(p) === "fonte" && p?.wattage && <Spec>{p.wattage}</Spec>}
        </div>
        {categoryOf(p) === "acessorios" && usesModels(accTypeOf(p)) && p?.compatible?.length === 1 && (
          <p data-testid={`product-compat-${p.id}`} className="mt-3 text-sm text-zinc-300">
            <span className="text-zinc-400">Disponível para: </span>{p.compatible.join(", ")}
          </p>
        )}
        <p data-testid={`product-warranty-${p.id}`} className="mt-3 flex items-center gap-2 text-sm font-medium text-emerald-300">
          <ShieldCheck className="h-4 w-4 shrink-0" /> {warrantyOf(p)}
        </p>
        {categoryOf(p) === "acessorios" && p?.accidental && (
          <div data-testid={`product-accidental-${p.id}`} className="mt-3 rounded-2xl border border-amber-300/30 bg-amber-300/10 px-3.5 py-2.5">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-200"><ShieldPlus className="h-4 w-4 shrink-0" /> Quebra acidental · 6 meses</p>
            <p className="mt-0.5 pl-6 text-xs text-zinc-300">Protege caso o aparelho quebre usando este acessório.</p>
          </div>
        )}
        {p?.notes && <p className="mt-2 line-clamp-2 text-sm text-zinc-400 leading-relaxed">{p.notes}</p>}
        <div className="mt-auto flex flex-col gap-3 pt-4">
          <div className="flex items-start justify-between gap-3 border-t border-white/10 pt-3">
            <p className="mono pt-2 text-[11px] uppercase tracking-[0.2em] text-zinc-400">Valor</p>
            <PriceBlock p={p} size="card" end priceTestid={`product-price-${p.id}`} installmentTestid={`product-installment-${p.id}`} />
          </div>
          <InterestAction p={p} preferredModel={preferredModel} />
        </div>
      </div>
    </motion.article>
  );
};
